import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { DetailHeader } from '@/components/detail-header';
import { Button, Card, CardHeader, CardTitle, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import { useInstance, useInstanceMonitoring } from '@/api/capacities-cloud';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatGb } from '../dashboard/capacity-format';
import { cloudStatusView, mbToGb } from './cloud-format';
import { toMonitoringSeries } from './instance-monitoring';

export interface CloudInstanceDetailPageProps {
  /** Instance id. Falls back to the `:id` route param when omitted. */
  instanceId?: string;
}

/**
 * Cloud Instance detail — identity header + overview Stats + CPU/memory
 * monitoring chart. The `/instances/*` endpoints are backend-flaky on staging
 * (401 customer-resolve); both queries degrade to a graceful EmptyState instead
 * of crashing. Read-only.
 */
export default function CloudInstanceDetailPage({ instanceId: idProp }: CloudInstanceDetailPageProps) {
  const params = useParams();
  const instanceId = idProp ?? params.id ?? '';

  const { data: instance, isLoading, isError, refetch } = useInstance(instanceId);
  const monitoring = useInstanceMonitoring(instanceId);

  const series = useMemo(() => toMonitoringSeries(monitoring.data), [monitoring.data]);
  const status = cloudStatusView(instance?.status);
  const name = instance?.name || instanceId;

  const info: InfoItem[] = instance
    ? [
        { label: 'Status', value: <StatusPill tone={status.tone}>{status.label}</StatusPill> },
        { label: 'vCPU', value: instance.vcpus ?? 0 },
        { label: 'Memory', value: formatGb(mbToGb(instance.memory_mb)) },
        { label: 'Host', value: instance.host_name || '—' },
        { label: 'Zone', value: instance.zone_name || '—' },
      ]
    : [];

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <DetailHeader
          parent={[
            { label: 'Capacities & Resources', to: '/capacities' },
            { label: 'Cloud Resources', to: '/capacities?tab=cloud' },
          ]}
          current={name}
          title={name}
          meta={
            !isLoading && instance ? (
              <StatusPill tone={status.tone}>{status.label}</StatusPill>
            ) : null
          }
        />

        {isLoading ? (
          <Skeleton style={{ height: 120 }} />
        ) : isError || !instance ? (
          <EmptyState
            title="Instance unavailable"
            description="The compute service could not resolve this instance right now. This endpoint is known to be intermittent on staging."
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <>
            <Card flat>
              <CardHeader>
                <CardTitle>Instance Information</CardTitle>
              </CardHeader>
              <CardBody>
                <InfoGrid items={info} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Utilization</CardTitle>
              </CardHeader>
              <CardBody>
              {monitoring.isLoading ? (
                <Skeleton style={{ height: 240 }} />
              ) : series.length > 0 ? (
                <Chart
                  type="line"
                  height={240}
                  data={series}
                  xKey="t"
                  series={[
                    { key: 'cpu', label: 'CPU %', color: 'var(--vn-primary-solid)' },
                    { key: 'memory', label: 'Memory %', color: 'var(--vn-success-solid)' },
                  ]}
                  showLegend
                  valueFormatter={(v) => `${typeof v === 'number' ? v : Number(v) || 0}%`}
                />
              ) : (
                <EmptyState
                  size="sm"
                  title="No monitoring data"
                  description="Utilization metrics are unavailable for this instance."
                />
              )}
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </Container>
  );
}
